import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ClientPortalOrganization } from '@oneup/shared';
import { getPortal, type PortalKey } from '@command-center/portal-registry';
import {
  initializeBrowserImpersonation,
  readBrowserImpersonation,
  type BrowserImpersonationState,
} from '@command-center/auth-sdk/browser-impersonation';
import { decideAuthEvent, isAuthoritativeDenial } from '@command-center/auth-sdk/session-events';
import {
  establishEmailAuthSession,
  readEmailAuthRequest,
  type EmailAuthSessionResult,
  type PasswordEmailAction,
} from './email-auth-flow';
import {
  authedFetch,
  clearLocalSession,
  getSupabaseAnonKey,
  getSupabaseBrowserClient,
  getSupabaseUrl,
  requestPasswordReset,
  signInWithPassword,
  signOut as supabaseSignOut,
  updatePassword,
  type SupabaseSession,
  type SupabaseUser,
} from './supabase-browser';

type AccessState = 'checking' | 'allowed' | 'denied';

export interface ClientActorProfile {
  userId: string;
  email: string | null;
  name: string | null;
}

export interface AuthContextValue {
  isLoading: boolean;
  isConfigured: boolean;
  session: SupabaseSession | null;
  user: SupabaseUser | null;
  actor: ClientActorProfile | null;
  /**
   * The organizations this user may select, straight from the bootstrap.
   *
   * Server-owned and re-derived from live membership on every verification.
   * The browser never adds to this list, and selecting from it authorizes
   * nothing on its own.
   */
  organizations: ClientPortalOrganization[];
  impersonation: BrowserImpersonationState | null;
  impersonationNotificationEditing: boolean;
  setImpersonationNotificationEditing: (enabled: boolean) => void;
  accessState: AccessState;
  accessError?: string;
  passwordEmailAction: PasswordEmailAction | null;
  emailAuthPortal: PortalKey | null;
  emailAuthComplete: { action: PasswordEmailAction; portalKey: PortalKey } | null;
  emailAuthError?: string;
  signIn: (email: string, password: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  signOut: () => Promise<void>;
  /** Re-run the bootstrap, blocking the portal as a first load would. */
  refreshAccess: () => void;
  requestPasswordReset: typeof requestPasswordReset;
  updatePassword: typeof updatePassword;
  completePasswordUpdate: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const API_BASE = ((import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
const IMPERSONATION_IDENTITY = 'impersonation';
const EMPTY_ORGANIZATIONS: ClientPortalOrganization[] = [];

interface AuthMeBody {
  hasAppAccess?: boolean;
  hasOrganizationAccess?: boolean;
  actor?: ClientActorProfile | null;
  organizations?: ClientPortalOrganization[];
  error?: { message?: string };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<SupabaseSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessState, setAccessState] = useState<AccessState>('checking');
  const [accessError, setAccessError] = useState<string>();
  const [actor, setActor] = useState<ClientActorProfile | null>(null);
  const [organizations, setOrganizations] = useState<ClientPortalOrganization[]>(EMPTY_ORGANIZATIONS);
  const [impersonation, setImpersonation] = useState<BrowserImpersonationState | null>(null);
  // Deliberately memory-only: a reload or a new support session starts read-only.
  const [impersonationNotificationEditing, setImpersonationNotificationEditing] = useState(false);
  const [passwordEmailAction, setPasswordEmailAction] = useState<PasswordEmailAction | null>(null);
  const [emailAuthPortal, setEmailAuthPortal] = useState<PortalKey | null>(null);
  const [emailAuthComplete, setEmailAuthComplete] = useState<{
    action: PasswordEmailAction;
    portalKey: PortalKey;
  } | null>(null);
  const [emailAuthError, setEmailAuthError] = useState<string>();
  const emailAuthRequest = useMemo(() => readEmailAuthRequest(window.location), []);
  const isConfigured = useMemo(() => Boolean(getSupabaseUrl() && getSupabaseAnonKey()), []);

  /** The last user id any auth event reported, approved or not. */
  const knownUserId = useRef<string | null>(null);
  /** The user id currently holding an `allowed` decision. */
  const allowedUserId = useRef<string | null>(null);
  /** Monotonic verification id. Only the newest run may write state. */
  const latestRun = useRef(0);
  /** An in-flight verification, so one identity never has two of them. */
  const inFlight = useRef<{ identity: string; promise: Promise<void> } | null>(null);
  /** One-time email tokens must survive React Strict Mode's effect replay. */
  const emailAuthInitialization = useRef<Promise<EmailAuthSessionResult> | null>(null);

  /**
   * Confirm this session's access against the Worker.
   *
   * Two rules make this safe to call as often as Supabase emits events:
   *
   * 1. It only blanks the portal when the identity being checked is not the one
   *    already approved. Re-confirming an approved user is invisible.
   * 2. Concurrent calls for the same identity share one request, and a slower
   *    older run can never overwrite a newer one's result.
   */
  const verifyAccess = useCallback(async (
    nextSession: SupabaseSession | null,
    options: { background?: boolean } = {},
  ): Promise<void> => {
    const identity = nextSession?.user.id
      ?? (readBrowserImpersonation() ? IMPERSONATION_IDENTITY : null);
    if (!identity) {
      latestRun.current += 1;
      inFlight.current = null;
      allowedUserId.current = null;
      setAccessState('checking');
      setAccessError(undefined);
      setActor(null);
      setOrganizations(EMPTY_ORGANIZATIONS);
      return;
    }

    // Revalidation of an identity the portal has already admitted. Nothing here
    // may take the workspace away except an authoritative answer.
    const isRevalidation = allowedUserId.current === identity;
    if (!options.background && !isRevalidation) {
      setAccessState('checking');
      setAccessError(undefined);
    }

    const pending = inFlight.current;
    if (pending?.identity === identity) return pending.promise;

    const run = (latestRun.current += 1);
    const commit = (apply: () => void) => {
      // A newer verification has started (or this identity was signed out).
      // Its answer wins regardless of which request returned first.
      if (run !== latestRun.current) return;
      apply();
    };

    const promise = (async () => {
      let response: Response | undefined;
      let body: AuthMeBody | null = null;
      try {
        response = await authedFetch(`${API_BASE}/api/auth/me`, { headers: { Accept: 'application/json' } });
        body = await response.json().catch(() => null) as AuthMeBody | null;
      } catch {
        response = undefined;
      }

      if (response?.ok && body?.hasAppAccess && body.hasOrganizationAccess) {
        commit(() => {
          allowedUserId.current = identity;
          setAccessState('allowed');
          setAccessError(undefined);
          setActor(body?.actor ?? null);
          setOrganizations(body?.organizations ?? EMPTY_ORGANIZATIONS);
        });
        return;
      }

      // A successful response saying "no access" is a decision. A transport
      // failure or a 5xx is not — during revalidation the portal keeps running
      // on the authorization it already has, and the API remains the boundary
      // that actually enforces it.
      if (isRevalidation && !isAuthoritativeDenial(response?.status)) return;

      commit(() => {
        allowedUserId.current = null;
        setAccessState('denied');
        setActor(null);
        setOrganizations(EMPTY_ORGANIZATIONS);
        setAccessError(
          response === undefined
            ? 'Your portal is temporarily unavailable. Please try again in a moment.'
            : body?.error?.message
              ?? (body?.hasAppAccess === false
                ? 'This account doesn’t have access to the portal yet. Contact One Up and we’ll set it up.'
                : 'This account isn’t connected to a business yet. Contact One Up and we’ll finish setting you up.'),
        );
      });
    })().finally(() => {
      if (inFlight.current?.promise === promise) inFlight.current = null;
    });

    inFlight.current = { identity, promise };
    return promise;
  }, []);

  /** Drop everything scoped to the person who was signed in. */
  const clearUserScopedState = useCallback(() => {
    latestRun.current += 1;
    inFlight.current = null;
    allowedUserId.current = null;
    knownUserId.current = null;
    queryClient.clear();
    setActor(null);
    setOrganizations(EMPTY_ORGANIZATIONS);
    setImpersonationNotificationEditing(false);
    setAccessState('checking');
    setAccessError(undefined);
  }, [queryClient]);

  useEffect(() => {
    let disposed = false;
    let unsubscribe: (() => void) | undefined;

    const subscribe = (supabase: NonNullable<ReturnType<typeof getSupabaseBrowserClient>>) => {
      const { data } = supabase.auth.onAuthStateChange((event, nextSession) => {
        const nextUserId = nextSession?.user.id ?? null;
        const decision = decideAuthEvent({
          event,
          nextUserId,
          knownUserId: knownUserId.current,
          allowedUserId: allowedUserId.current,
        });
        setSession(nextSession);
        if (event === 'PASSWORD_RECOVERY' && window.location.pathname.replace(/\/+$/, '') === '/auth/reset') {
          setPasswordEmailAction('recovery');
          setEmailAuthError(undefined);
        }

        switch (decision.kind) {
          case 'sign-out':
            clearUserScopedState();
            setPasswordEmailAction(null);
            return;
          case 'switch-identity':
            clearUserScopedState();
            knownUserId.current = nextUserId;
            void verifyAccess(nextSession);
            return;
          case 'verify':
            knownUserId.current = nextUserId;
            void verifyAccess(nextSession, { background: !decision.blocking });
            return;
          case 'ignore':
            knownUserId.current = nextUserId ?? knownUserId.current;
        }
      });
      unsubscribe = () => data.subscription.unsubscribe();
    };

    const initialize = async () => {
      if (emailAuthRequest.kind !== 'none') {
        setEmailAuthPortal(emailAuthRequest.portalKey);
        const supabase = getSupabaseBrowserClient();
        if (!supabase) {
          if (!disposed) {
            setIsLoading(false);
            setEmailAuthError('This authentication link cannot be opened right now. Please contact One Up.');
          }
          return;
        }
        const initialization = emailAuthInitialization.current
          ?? establishEmailAuthSession(supabase, emailAuthRequest);
        emailAuthInitialization.current = initialization;
        const result = await initialization;
        if (disposed) return;
        if (!result.ok) {
          setIsLoading(false);
          setEmailAuthError(result.message);
          return;
        }
        const portal = getPortal(result.portalKey);
        const safePath = result.action === 'recovery'
          ? portal.auth.recoveryPath
          : portal.auth.invitePath;
        window.history.replaceState({}, '', safePath);
        const accessResponse = await authedFetch(
          `${API_BASE}/api/auth/portal-access?portal=${encodeURIComponent(result.portalKey)}`,
          { headers: { Accept: 'application/json' } },
        );
        const accessBody = await accessResponse.json().catch(() => null) as
          | { authorized?: boolean; error?: { message?: string } }
          | null;
        if (disposed) return;
        if (!accessResponse.ok || !accessBody?.authorized) {
          await clearLocalSession();
          setSession(null);
          setIsLoading(false);
          setEmailAuthError(
            accessBody?.error?.message
              ?? `This account is not authorized for ${portal.appName}. Ask One Up to review the portal assignment.`,
          );
          return;
        }
        knownUserId.current = result.session.user.id;
        setSession(result.session);
        setPasswordEmailAction(result.action);
        setEmailAuthPortal(result.portalKey);
        setEmailAuthError(undefined);
        setIsLoading(false);
        subscribe(supabase);
        return;
      }

      const supportSession = await initializeBrowserImpersonation(API_BASE);
      if (disposed) return;
      setImpersonation(supportSession);
      if (supportSession) {
        setIsLoading(false);
        await verifyAccess(null);
        return;
      }
      const supabase = getSupabaseBrowserClient();
      if (!supabase) { setIsLoading(false); setAccessState('denied'); return; }
      const { data: current } = await supabase.auth.getSession();
      if (disposed) return;
      knownUserId.current = current.session?.user.id ?? null;
      setSession(current.session);
      setIsLoading(false);
      // Deliberately not awaited: `onAuthStateChange` fires INITIAL_SESSION
      // synchronously on subscribe and is deduplicated against this same
      // request, so the bootstrap stays a single round trip either way.
      void verifyAccess(current.session);
      subscribe(supabase);
    };

    void initialize().catch(async (error: unknown) => {
      if (disposed) return;
      setIsLoading(false);
      if (emailAuthRequest.kind !== 'none') {
        await clearLocalSession().catch(() => undefined);
        setSession(null);
        setEmailAuthError('This authentication link could not be verified. Please request a new link.');
      } else {
        setAccessState('denied');
        setAccessError(error instanceof Error ? error.message : 'The support session could not be opened.');
      }
    });
    return () => {
      disposed = true;
      unsubscribe?.();
    };
  }, [clearUserScopedState, emailAuthRequest, verifyAccess]);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await signInWithPassword(email, password);
    if (!result.ok) return result;
    setSession(result.session);
    knownUserId.current = result.session.user.id;
    // Supabase emits SIGNED_IN for this same sign-in. Both paths land on the
    // one in-flight verification for this identity rather than racing.
    await verifyAccess(result.session);
    return { ok: true as const };
  }, [verifyAccess]);

  const signOut = useCallback(async () => {
    await supabaseSignOut();
    clearUserScopedState();
    setSession(null);
  }, [clearUserScopedState]);

  const refreshAccess = useCallback(() => {
    // Treat this as a first load again: the user asked, so blocking is honest,
    // and it must not be answered by a request that is already in flight.
    allowedUserId.current = null;
    inFlight.current = null;
    void verifyAccess(session);
  }, [session, verifyAccess]);

  const completePasswordUpdate = useCallback(async () => {
    const action = passwordEmailAction;
    const portalKey = emailAuthPortal;
    if (!action || !portalKey) return;
    window.history.replaceState({}, '', '/');
    // End only the temporary account-management session in this browser. The
    // destination portal establishes its own session through normal sign-in;
    // no browser session or token crosses domains.
    await clearLocalSession();
    clearUserScopedState();
    setSession(null);
    setPasswordEmailAction(null);
    setEmailAuthError(undefined);
    setEmailAuthComplete({ action, portalKey });
  }, [clearUserScopedState, emailAuthPortal, passwordEmailAction]);

  const value = useMemo<AuthContextValue>(() => ({
    isLoading, isConfigured, session, user: session?.user ?? null, actor, organizations, impersonation,
    impersonationNotificationEditing, setImpersonationNotificationEditing,
    accessState, accessError, passwordEmailAction, emailAuthPortal, emailAuthComplete,
    emailAuthError, signIn, signOut, refreshAccess,
    requestPasswordReset, updatePassword, completePasswordUpdate,
  }), [isLoading, isConfigured, session, actor, organizations, impersonation, impersonationNotificationEditing, accessState, accessError,
    passwordEmailAction, emailAuthPortal, emailAuthComplete, emailAuthError,
    signIn, signOut, refreshAccess, completePasswordUpdate]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within an AuthProvider.');
  return value;
}
